<script lang="ts">
    import {KpLoadableContainer} from 'shared/layouts';
    import {FileViewForm} from 'shared/constants';
    import type {Form, ReportTemplate} from 'src/typings';
    import type {ExportFilePreviewData} from './types';
    import {getReportServerContext} from 'src/features/report-server/report-server-context';
    import {onDestroy} from 'svelte';
    import {get} from 'svelte/store';
    import {exists} from 'shared/utils/custom-utils';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import ExportForm from './ExportForm.svelte';
    import ExportPreview from './ExportPreview.svelte';

    const context = getReportServerContext();

    let loading = false;
    let selectedTemplate: ReportTemplate | null = null;
    let reportTemplateForm: Form | null = null;
    let filePreviewData: ExportFilePreviewData | null = null; // URL for media viewer file preview
    let exportData: Record<string, any> = {};
    let displayForm = false;
    let isFormSubmitting = false;

    const reportTemplateUnsubscribe = context.selectedTemplate.subscribe((reportTemplate) => {
        if (selectedTemplate?.id === reportTemplate?.id) {
            return;
        }

        selectedTemplate = reportTemplate;

        if (!exists(reportTemplate)) {
            return;
        }

        loadReportTemplateForm(reportTemplate);
    });

    onDestroy(() => {
        reportTemplateUnsubscribe();
    });

    async function loadReportTemplateForm(reportTemplate: ReportTemplate) {
        loading = true;

        displayForm = false;
        filePreviewData = null;
        exportData = get(context.prefillTemplateProps);
        const loadedReportTemplateForm = await context.service.getTemplateForm(reportTemplate);

        const prefilledKeys = Object.keys(exportData);
        const unfilledFields = loadedReportTemplateForm.fields.filter((field) => !prefilledKeys.includes(field.fieldName));

        reportTemplateForm = {
            ...loadedReportTemplateForm,
            fields: unfilledFields
        };

        if (unfilledFields.length === 0) {
            isFormSubmitting = true;
            await previewExport();
            isFormSubmitting = false;
        } else {
            displayForm = true;
        }

        loading = false;
    }

    function resolveFileViewFormFromFilename(file: File): FileViewForm {
        if (file.type.includes('application/pdf')) {
            return FileViewForm.PDF;
        }

        if (file.type.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
            return FileViewForm.SPREADSHEET;
        }

        return FileViewForm.UNKNOWN;
    }

    async function previewExport() {
        const blobExportFile = await context.service.getBlobExport(selectedTemplate, exportData);

        filePreviewData = {
            viewForm: resolveFileViewFormFromFilename(blobExportFile),
            fileUrl: URL.createObjectURL(blobExportFile)
        }
    }

    const handleCloseClick = () => {
        context.setSelectedTemplate(null);
    };

    const handleFormSubmit = async (event: CustomEvent<Record<string, any>>) => {
        exportData = {
            ...event.detail,
            ...get(context.prefillTemplateProps)
        };
        await previewExport();
        displayForm = false;
    };
</script>

<div class="export-form-container">
    <KpLoadableContainer fillAvailableSpace {loading}>
        {#if exists(selectedTemplate)}
            {#if displayForm}
                <ExportForm formSettings={reportTemplateForm}
                            on:form-submit={handleFormSubmit}
                            on:close={handleCloseClick}
                            {isFormSubmitting}/>
            {:else}
                <ExportPreview {filePreviewData}
                               {exportData}
                               {selectedTemplate}
                               on:close={handleCloseClick}/>
            {/if}
        {/if}

        {#if !exists(selectedTemplate)}
            <IconedContent icon="info" align="center" justify="center" orientation="vertical" fillAvailableSpace>
                Nejdříve otevřete tiskovou sestavu
            </IconedContent>
        {/if}
    </KpLoadableContainer>
</div>

<style lang="less">
    @import (reference) "styles/portaro-erp.less";

    .export-form-container {
        .flex-grow();
        width: calc(100% - var(--sidebar-width));
    }
</style>